import React, { useState, useEffect, useCallback } from 'react';
import { Share2 } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { getSession, subscribeToTripCollaboration } from '../../lib/supabase';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';

const AVATAR_COLORS = [
  'bg-teal-500', 'bg-indigo-500', 'bg-rose-500', 'bg-amber-500',
  'bg-emerald-500', 'bg-purple-500', 'bg-sky-500', 'bg-orange-500',
];

function getAvatarColor(userId) {
  if (!userId) return AVATAR_COLORS[0];
  let hash = 0;
  for (let i = 0; i < userId.length; i++) {
    hash = userId.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

function getInitials(name) {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return parts[0].slice(0, 2).toUpperCase();
}

const MAX_VISIBLE = 3;

export default function CollaboratorAvatars({ tripId, onShareClick, onActivityLogClick }) {
  const { user } = useAuth();
  const [collaborators, setCollaborators] = useState([]);
  const [onlineUserIds, setOnlineUserIds] = useState(new Set());
  const [loading, setLoading] = useState(true);

  // Get auth headers by reading token from localStorage (fast) or getSession() (fallback)
  const getAuthHeaders = useCallback(async () => {
    const headers = { 'Content-Type': 'application/json' };
    try {
      const storageKey = Object.keys(localStorage).find(k => k.startsWith('sb-') && k.endsWith('-auth-token'));
      if (storageKey) {
        const stored = JSON.parse(localStorage.getItem(storageKey) || '{}');
        if (stored?.access_token) {
          headers['Authorization'] = `Bearer ${stored.access_token}`;
          return headers;
        }
      }
      const session = await getSession();
      if (session?.access_token) {
        headers['Authorization'] = `Bearer ${session.access_token}`;
      }
    } catch { /* proceed without auth */ }
    return headers;
  }, []);

  // Fetch collaborators
  useEffect(() => {
    if (!tripId) return;

    (async () => {
      const headers = await getAuthHeaders();
      fetch(`${BACKEND_URL}/api/trips/${tripId}/collaborators`, {
        headers,
      })
        .then(res => res.ok ? res.json() : Promise.reject(res))
        .then(data => {
          setCollaborators(Array.isArray(data) ? data : data.collaborators || []);
        })
        .catch(err => {
          console.error('Failed to fetch collaborators:', err);
        })
        .finally(() => setLoading(false));
    })();
  }, [tripId, getAuthHeaders]);

  // Subscribe to presence for online status
  useEffect(() => {
    if (!tripId) return;

    const unsubscribe = subscribeToTripCollaboration(tripId, {
      onPresence: (state) => {
        const online = new Set();
        Object.values(state).forEach(presences => {
          presences.forEach(p => {
            if (p.user_id) online.add(p.user_id);
          });
        });
        setOnlineUserIds(online);
      },
    });

    return unsubscribe;
  }, [tripId]);

  if (loading || collaborators.length === 0) {
    return (
      <button
        onClick={onShareClick}
        className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-teal-600 hover:bg-teal-50 rounded-lg transition-colors"
      >
        <Share2 className="w-4 h-4" />
        <span className="font-medium">Share</span>
      </button>
    );
  }

  const visible = collaborators.slice(0, MAX_VISIBLE);
  const overflow = collaborators.length - MAX_VISIBLE;

  return (
    <div className="flex items-center gap-2">
      {/* Avatar stack */}
      <button
        onClick={onActivityLogClick}
        className="flex items-center -space-x-2 hover:opacity-80 transition-opacity"
        title="View trip activity"
      >
        {visible.map((collab, idx) => {
          const isOnline = onlineUserIds.has(collab.user_id);
          return (
            <div key={collab.user_id || collab.userId || `collab-${idx}`} className="relative">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold border-2 border-white ${getAvatarColor(collab.user_id)}`}
              >
                {getInitials(collab.display_name || collab.email)}
              </div>
              {isOnline && (
                <span className="absolute bottom-0 right-0 w-2.5 h-2.5 bg-green-400 border-2 border-white rounded-full" />
              )}
            </div>
          );
        })}
        {overflow > 0 && (
          <div className="w-8 h-8 rounded-full flex items-center justify-center bg-gray-200 text-gray-600 text-xs font-bold border-2 border-white">
            +{overflow}
          </div>
        )}
      </button>

      {/* Share button */}
      <button
        onClick={onShareClick}
        className="p-2 rounded-lg hover:bg-teal-50 text-teal-600 transition-colors"
        title="Share trip"
      >
        <Share2 className="w-4 h-4" />
      </button>
    </div>
  );
}
